(function initVanityBuilderLoader() {
    const loaderScript = document.currentScript;
    const requestedBuilderScriptSrc = loaderScript?.dataset.builderScriptSrc;

    if (window.VanityBuilderLoader) {
        if (requestedBuilderScriptSrc) {
            window.VanityBuilderLoader.configure(requestedBuilderScriptSrc);
        }
        window.VanityBuilderLoader.init(document);
        return;
    }

    if (!requestedBuilderScriptSrc) return;

    window.__vanityBuilderLoaderInitialized = true;

    let builderScriptSrc = requestedBuilderScriptSrc;
    let runtimePromise;
    const builderPromises = new WeakMap();
    const initializedHosts = new WeakSet();

    function findElement(root, selector) {
        if (root instanceof Element && root.matches(selector)) return root;
        return root?.querySelector?.(selector) || null;
    }

    function getBuilder(root = document) {
        return findElement(root, 'vanity-builder');
    }

    function getBuilderHost(root = document) {
        return findElement(root, '[data-vanity-builder-host]');
    }

    function isStaleHostError(error) {
        return error?.name === 'AbortError' || error?.name === 'VanityBuilderStaleHostError';
    }

    function reportLoadError(error) {
        if (!isStaleHostError(error)) {
            console.error('Unable to load vanity builder', error);
        }
    }

    function setControlsLoading(isLoading) {
        document.querySelectorAll('[data-vanity-builder-scroll]').forEach(control => {
            control.classList.toggle('custom-loading', isLoading);
            control.setAttribute('aria-busy', isLoading ? 'true' : 'false');
            control.querySelector('.loader')?.classList.toggle('d-none', !isLoading);
        });
    }

    function setHostState(host, state) {
        if (!host?.isConnected) return;

        host.dataset.state = state;
        host.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');

        const loadingStatus = host.querySelector('[data-vanity-builder-loading]');
        const errorStatus = host.querySelector('[data-vanity-builder-error]');
        if (loadingStatus) loadingStatus.hidden = state !== 'loading';
        if (errorStatus) errorStatus.hidden = state !== 'error';
    }

    function loadBuilderMarkup(root = document) {
        const existingBuilder = getBuilder(root);
        if (existingBuilder) return Promise.resolve(existingBuilder);

        const host = getBuilderHost(root);
        const fragmentUrl = host?.dataset.builderFragmentUrl;
        if (!host || !fragmentUrl) return Promise.reject(new Error('Vanity builder host is unavailable'));
        if (builderPromises.has(host)) return builderPromises.get(host);

        setHostState(host, 'loading');

        const builderPromise = fetch(fragmentUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
            priority: 'low',
        })
            .then(response => {
                if (!response.ok) throw new Error(`Vanity builder request failed with status ${response.status}`);
                return response.text();
            })
            .then(markup => {
                if (!host.isConnected) {
                    const error = new Error('Vanity builder host was replaced before loading completed');
                    error.name = 'VanityBuilderStaleHostError';
                    throw error;
                }

                const template = document.createElement('template');
                template.innerHTML = markup.trim();

                const builder = template.content.querySelector('vanity-builder');
                if (!builder) throw new Error('Vanity builder response did not contain a builder');

                builder.id = host.id;
                host.replaceWith(builder);
                document.dispatchEvent(new CustomEvent('vanity-builder:loaded', { detail: { builder } }));

                return builder;
            })
            .catch(error => {
                builderPromises.delete(host);
                if (!isStaleHostError(error)) setHostState(host, 'error');
                throw error;
            });

        builderPromises.set(host, builderPromise);
        return builderPromise;
    }

    function loadRuntime() {
        if (customElements.get('vanity-builder')) return Promise.resolve();
        if (runtimePromise) return runtimePromise;

        runtimePromise = new Promise((resolve, reject) => {
            const existingScript = document.querySelector('[data-vanity-builder-runtime]');

            if (existingScript) {
                if (existingScript.dataset.loaded === 'true' || customElements.get('vanity-builder')) {
                    resolve();
                    return;
                }

                existingScript.addEventListener('load', resolve, { once: true });
                existingScript.addEventListener('error', reject, { once: true });
                return;
            }

            const script = document.createElement('script');
            script.src = builderScriptSrc;
            script.async = true;
            script.fetchPriority = window.__vanityBuilderInteractionRequested ? 'high' : 'low';
            script.dataset.vanityBuilderRuntime = '';
            script.addEventListener(
                'load',
                () => {
                    script.dataset.loaded = 'true';
                    resolve();
                },
                { once: true },
            );
            script.addEventListener(
                'error',
                error => {
                    script.remove();
                    reject(error);
                },
                { once: true },
            );
            document.head.appendChild(script);
        }).catch(error => {
            runtimePromise = undefined;
            throw error;
        });

        return runtimePromise;
    }

    function loadAndUpgrade(root = document) {
        return Promise.all([loadBuilderMarkup(root), loadRuntime()])
            .then(([builder]) => customElements.whenDefined('vanity-builder').then(() => builder));
    }

    function replayInteraction(event, target) {
        event.preventDefault();
        event.stopImmediatePropagation();

        window.__vanityBuilderInteractionRequested = true;
        setControlsLoading(true);

        loadAndUpgrade()
            .then(() => {
                setControlsLoading(false);
                if (target.isConnected) window.requestAnimationFrame(() => target.click());
            })
            .catch(error => {
                setControlsLoading(false);
                reportLoadError(error);
            });
    }

    document.addEventListener(
        'click',
        event => {
            const retryControl = event.target.closest('[data-vanity-builder-retry]');
            if (retryControl) {
                event.preventDefault();
                window.__vanityBuilderInteractionRequested = true;
                setControlsLoading(true);
                loadAndUpgrade(retryControl.closest('[data-vanity-builder-host]') || document)
                    .then(builder => {
                        setControlsLoading(false);
                        builder.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    })
                    .catch(error => {
                        setControlsLoading(false);
                        reportLoadError(error);
                    });
                return;
            }

            const builderControl = event.target.closest('[data-vanity-builder-scroll]');
            const builderInteraction = event.target.closest('vanity-builder button, vanity-builder input, vanity-builder a');
            const target = builderControl || builderInteraction;
            if (!target) return;

            if (getBuilder() && customElements.get('vanity-builder')) return;

            replayInteraction(event, target);
        },
        true,
    );

    const intersectionObserver = 'IntersectionObserver' in window
        ? new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return;

                    intersectionObserver.unobserve(entry.target);
                    loadAndUpgrade(entry.target).catch(reportLoadError);
                });
            },
            { rootMargin: '600px 0px' },
        )
        : null;

    function initializeHost(host) {
        if (!host?.isConnected || initializedHosts.has(host)) return;

        initializedHosts.add(host);
        intersectionObserver?.observe(host);

        const loadWhenIdle = () => {
            if (!host.isConnected || getBuilder()) return;
            loadAndUpgrade(host).catch(reportLoadError);
        };

        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(loadWhenIdle, { timeout: 8000 });
        } else {
            window.setTimeout(loadWhenIdle, 4000);
        }
    }

    function initialize(root = document) {
        const builder = getBuilder(root);
        if (builder && !customElements.get('vanity-builder')) {
            loadRuntime().catch(reportLoadError);
        }

        if (root instanceof Element && root.matches('[data-vanity-builder-host]')) {
            initializeHost(root);
        }

        root.querySelectorAll?.('[data-vanity-builder-host]').forEach(initializeHost);
    }

    window.VanityBuilderLoader = {
        configure(src) {
            if (src) builderScriptSrc = src;
        },
        init: initialize,
        load: loadAndUpgrade,
    };

    document.addEventListener('theme:product-loaded', () => initialize(document));
    document.addEventListener('shopify:section:load', event => initialize(event.target));
    window.addEventListener('pageshow', () => initialize(document));

    const startAfterInitialPaint = () => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => initialize(document));
        });
    };

    if (document.readyState === 'complete') {
        startAfterInitialPaint();
    } else {
        window.addEventListener('load', startAfterInitialPaint, { once: true });
    }
})();
